// ============================================
// Event Live Board
// ============================================

import React from 'react';
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { Event, EventTherapist, BookingRequest, Session, STATUS_COLORS } from '../../../types';

interface Props {
  event: Event;
}

export default function EventLiveBoard({ event }: Props) {
  const [therapists, setTherapists] = React.useState<EventTherapist[]>([]);
  const [pendingRequests, setPendingRequests] = React.useState<BookingRequest[]>([]);
  const [activeSessions, setActiveSessions] = React.useState<Session[]>([]);

  React.useEffect(() => {
    // Subscribe to therapists
    const therapistsUnsub = onSnapshot(
      collection(db, 'events', event.id, 'therapists'),
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          uid: doc.id,
          ...doc.data(),
        })) as EventTherapist[];
        setTherapists(data);
      }
    );

    // Subscribe to pending/accepted requests
    const requestsQuery = query(
      collection(db, 'events', event.id, 'bookingRequests'),
      where('status', 'in', ['pending', 'assigned', 'accepted']),
      orderBy('createdAt', 'asc')
    );
    const requestsUnsub = onSnapshot(requestsQuery, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as BookingRequest[];
      setPendingRequests(data);
    });

    // Subscribe to active sessions
    const sessionsQuery = query(
      collection(db, 'events', event.id, 'sessions'),
      where('status', '==', 'active')
    );
    const sessionsUnsub = onSnapshot(sessionsQuery, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Session[];
      setActiveSessions(data);
    });

    return () => {
      therapistsUnsub();
      requestsUnsub();
      sessionsUnsub();
    };
  }, [event.id]);

  const getTherapistById = (uid: string) => therapists.find((t) => t.uid === uid);

  const now = new Date();
  const offlineThreshold = new Date(now.getTime() - event.offlineThresholdSec * 1000);

  const isOffline = (therapist: EventTherapist) => {
    if (!therapist.lastHeartbeatAt) return true;
    const lastHeartbeat = therapist.lastHeartbeatAt.toDate();
    return lastHeartbeat < offlineThreshold;
  };

  // Stats
  const onDutyCount = therapists.filter((t) => t.dutyStatus === 'on_duty').length;
  const activeSessionCount = activeSessions.length;
  const pendingCount = pendingRequests.filter((r) => r.status === 'pending').length;
  const acceptedCount = pendingRequests.filter((r) => r.status === 'accepted').length;
  const offlineOnDutyCount = therapists.filter((t) => t.dutyStatus === 'on_duty' && isOffline(t)).length;

  return (
    <div className="live-board">
      {/* Stats Bar */}
      <div className="stats-bar">
        <div className="stat">
          <span className="stat-value">{onDutyCount}</span>
          <span className="stat-label">On Duty</span>
        </div>
        <div className="stat">
          <span className="stat-value">{activeSessionCount}</span>
          <span className="stat-label">Active Sessions</span>
        </div>
        <div className="stat">
          <span className="stat-value">{pendingCount}</span>
          <span className="stat-label">Pending</span>
        </div>
        <div className="stat">
          <span className="stat-value">{acceptedCount}</span>
          <span className="stat-label">Accepted</span>
        </div>
        {offlineOnDutyCount > 0 && (
          <div className="stat warning">
            <span className="stat-value">{offlineOnDutyCount}</span>
            <span className="stat-label">Offline (Working)</span>
          </div>
        )}
      </div>

      <div className="live-board-grid">
        {/* Therapists Panel */}
        <section className="board-section therapists-section">
          <h3>Therapists</h3>
          <div className="therapist-grid">
            {therapists.length === 0 ? (
              <div className="empty">No therapists joined yet</div>
            ) : (
              therapists.map((therapist) => (
                <div
                  key={therapist.uid}
                  className={`therapist-card ${isOffline(therapist) ? 'offline' : ''}`}
                >
                  <div style={{ position: 'relative' }}>
                    {/* Photo with status border */}
                    <div style={{
                      width: '54px',
                      height: '54px',
                      borderRadius: '50%',
                      border: `3px solid ${STATUS_COLORS[therapist.uiStatus]}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      {therapist.photoUrl && therapist.photoStatus === 'approved' ? (
                        <img
                          src={therapist.thumbnailUrl || therapist.photoUrl}
                          alt={therapist.displayName}
                          style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          backgroundColor: 'var(--color-border)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 600,
                        }}>
                          {therapist.displayName.charAt(0)}
                        </div>
                      )}
                    </div>
                    {therapist.photoStatus === 'pending' && (
                      <div style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: '#f59e0b',
                        color: '#fff',
                        fontSize: '0.6rem',
                        textAlign: 'center',
                        padding: '1px',
                        borderRadius: '0 0 4px 4px',
                      }}>
                        PENDING
                      </div>
                    )}
                  </div>
                  <div className="therapist-name">{therapist.displayName}</div>
                  <div className="therapist-status">{therapist.dutyStatus.replace('_', ' ')}</div>
                  {isOffline(therapist) && <div className="offline-badge">OFFLINE</div>}
                </div>
              ))
            )}
          </div>
        </section>

        {/* Requests Panel */}
        <section className="board-section requests-section">
          <h3>Pending & Accepted Requests</h3>
          <div className="requests-list">
            {pendingRequests.length === 0 ? (
              <div className="empty">No pending requests</div>
            ) : (
              pendingRequests.map((request) => {
                const therapist = request.assignedTherapistId
                  ? getTherapistById(request.assignedTherapistId)
                  : null;
                return (
                  <div key={request.id} className={`request-card ${request.status}`}>
                    <div className="request-location">
                      Table {request.tableNo}, Seat {request.seatNo}
                    </div>
                    <div className="request-minutes">{request.requestedMinutes} min</div>
                    <div className="request-status">{request.status}</div>
                    {therapist && (
                      <div className="request-therapist">
                        {therapist.displayName}
                        {request.etaMinutes && ` • ETA ${request.etaMinutes}m`}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </section>

        {/* Active Sessions Panel */}
        <section className="board-section sessions-section">
          <h3>Active Sessions</h3>
          <div className="sessions-list">
            {activeSessions.length === 0 ? (
              <div className="empty">No active sessions</div>
            ) : (
              activeSessions.map((session) => {
                const therapist = getTherapistById(session.therapistId);
                const endsAt = session.endsAt.toDate();
                const remaining = Math.max(0, Math.floor((endsAt.getTime() - now.getTime()) / 60000));
                const isEnding = remaining <= event.orangeThresholdMin;

                return (
                  <div key={session.id} className={`session-card ${isEnding ? 'ending-soon' : ''}`}>
                    <div className="session-location">
                      Table {session.tableNo}, Seat {session.seatNo}
                    </div>
                    <div className="session-therapist">
                      {therapist?.displayName || 'Unknown'}
                    </div>
                    <div className={`session-time ${isEnding ? 'warning' : ''}`}>
                      {remaining}m left
                    </div>
                    <div className="session-total">{session.totalMinutes} min total</div>
                  </div>
                );
              })
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
